import { logUsage } from "owa-analytics";
import type { MailboxInfo } from "owa-client-types";
import type { SubstratePeopleKeywordPersonSuggestion, SubstrateSearchSuggestionsResponseTextSuggestion, } from "../data/schema/SubstrateSearchSuggestionsResponse";

export interface PeopleKeywordSuggestionDetails {
    keyword: string;
    person: SubstratePeopleKeywordPersonSuggestion;
}

export type PeopleKeywordSuggestionValidationFailureReason = "UnsupportedType" | "MissingReferenceId" | "MissingKeyword" | "MissingPerson" | "MissingEmailAddress" | "MalformedEmailAddress";

export type PeopleKeywordSuggestionValidationResult = {
    details: PeopleKeywordSuggestionDetails;
    failureReason?: undefined;
} | {
    details?: undefined;
    failureReason: PeopleKeywordSuggestionValidationFailureReason;
};

interface PeopleKeywordSuggestionTelemetryContext {
    position: number;
    mailboxInfo: MailboxInfo;
}

export default function getPeopleKeywordSuggestionDetails(suggestion: SubstrateSearchSuggestionsResponseTextSuggestion, telemetryContext?: PeopleKeywordSuggestionTelemetryContext): PeopleKeywordSuggestionDetails | undefined {
    return getPeopleKeywordSuggestionValidationResult(suggestion, telemetryContext).details;
}

export function getPeopleKeywordSuggestionValidationResult(suggestion: SubstrateSearchSuggestionsResponseTextSuggestion, telemetryContext?: PeopleKeywordSuggestionTelemetryContext): PeopleKeywordSuggestionValidationResult {
    if (suggestion.TextSuggestionType !== "PeopleKeywordAsText") {
        return { failureReason: "UnsupportedType" };
    }
    
    const validationResult = validatePeopleKeywordSuggestion(suggestion);
    if (telemetryContext) {
        logUsage("SearchPeopleKeywordSuggestionConversion", telemetryContext.mailboxInfo, validationResult.details
            ? {
                textSuggestionType: "PeopleKeywordAsText",
                position: telemetryContext.position,
                conversionOutcome: "Success",
                failureReason: "None",
            }
            : {
                textSuggestionType: "PeopleKeywordAsText",
                position: telemetryContext.position,
                conversionOutcome: "Failure",
                failureReason: validationResult.failureReason,
            });
    }
    
    return validationResult;
}

function validatePeopleKeywordSuggestion(suggestion: SubstrateSearchSuggestionsResponseTextSuggestion): PeopleKeywordSuggestionValidationResult {
    if (!getNonEmptyString(suggestion.ReferenceId)) {
        return { failureReason: "MissingReferenceId" };
    }
    
    const keyword = getNonEmptyString(suggestion.Keyword);
    if (!keyword) {
        return { failureReason: "MissingKeyword" };
    }
    
    const person = suggestion.PersonSuggestions?.[0];
    if (!person) {
        return { failureReason: "MissingPerson" };
    }
    
    if (!Array.isArray(person.EmailAddresses) || person.EmailAddresses.length === 0) {
        return { failureReason: "MissingEmailAddress" };
    }
    
    const validEmailAddresses = person.EmailAddresses.reduce<string[]>((emailAddresses, emailAddress) => {
        if (typeof emailAddress === "string") {
            const normalizedEmailAddress = emailAddress.trim();
            if (normalizedEmailAddress) {
                emailAddresses.push(normalizedEmailAddress);
            }
        }
        return emailAddresses;
    }, []);
    if (validEmailAddresses.length === 0) {
        return { failureReason: "MalformedEmailAddress" };
    }
    
    return {
        details: {
            keyword,
            person: {
                ...person,
                EmailAddresses: validEmailAddresses,
            },
        },
    };
}

function getNonEmptyString(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value.trim() : undefined;
}
